import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { atom, useAtom } from 'jotai'
import * as Sentry from '@sentry/electron/renderer'
import { Virtuoso } from 'react-virtuoso'
import { twMerge } from 'tailwind-merge'
import { LoadingState } from '@highlight/ui'
import { useSortPreference } from '@renderer/hooks/use-sort-preferences'
import { PinnedTodoReorder, DraggableTodoRow, PinnedTodoDropZone, PINNED_TODOS_SORT_KEY } from './pinned-todo-reorder'
import { MOUNT_ALL_VIEWPORT_BY } from '@renderer/utils/virtualization-mount-all'
import { useForYouChatsFeed } from '@renderer/overlay/hooks/use-running-chats'
import { isPreviewlessStreamingChat, type SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import { useForYouDisplayChats, useForYouExecutedLinger } from '@renderer/overlay/hooks/for-you-linger'
import {
  useVisibleStagedActions,
  useSetStagedActionsPinned,
  type StagedAction,
} from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { ReminderCell } from '../../reminders/reminders-cell'
import { ReminderInput } from '../../reminders/reminders-input'
import { useReminderActions } from '../../../hooks/use-reminders'
import { useRemindersData } from '../../../reminders-context'
import { useRemindersSurfacePresence, useRetireLingersWhenIdle } from '../../../hooks/reminder-linger'
import { useSendClearedTodos } from '../../../hooks/use-send-cleared-todos'
import { useTodosUndoToast } from './use-todos-undo-toast'
import { useDueClock } from '../../reminders/hooks/use-due-clock'
import { useSidepanelFindInPageOpen } from '../../../atoms'
import { SIDEPANEL_FILLED_HOVER_SURFACE } from '../../cells/surface'
import { HomeHeading, HomeHeadingCollapse } from '../nested/home-heading'
import { NotificationsTodo } from '../nested/notifications-todo'
import { HomeViewMore } from '../nested/home-view-more'
import { ChatsInProgress, ForYouChatRow } from './running-chats'
import {
  groupTodos,
  isNotificationChat,
  isPinnedChat,
  listTodoRows,
  TODO_GROUP_PAGE_SIZE,
  todoGroupLimit,
  todoRowKey,
  type TodoEntry,
  type TodoGroup,
  type TodoGroupLimits,
  type TodoRow,
  type TodoRowPresence as TodoRowPresenceKind,
} from './todos-order'

const LOADING_MESSAGES = ['Loading to-dos…', 'Getting things ready…', 'Almost there…']

const NORMAL_VIEWPORT_BY = { top: 200, bottom: 200 }

// Outlive the section, so a group keeps its rows when Home remounts after a View closes.
const todoGroupLimitsAtom = atom<TodoGroupLimits>(new Map())
const notificationsTodoOpenAtom = atom(false)

// Home's body owns the scroller, so the list finds it by walking up.
function findScrollParent(element: HTMLElement | null): HTMLElement | null {
  let node = element?.parentElement ?? null
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}

/**
 * For You and Reminders as one list, split into Pinned, Due Soon and To-dos.
 * Highlight has already started every For You row, so its mark is part filled. A reminder's mark
 * starts empty, and turns orange on the day it's due and red once it's overdue.
 *
 * Each group heads itself, with no section header over the list. To-dos loads
 * the next For You page whenever it's asked to show more than it holds.
 */
export const SidepanelTodosSection = memo(function SidepanelTodosSection() {
  const {
    data: chats,
    isLoading: isLoadingChats,
    isError: isChatsError,
    feedKey,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useForYouChatsFeed()
  const displayChats = useForYouDisplayChats(chats, feedKey)
  const { activeItems, isLoading: isLoadingReminders } = useRemindersData()
  // Drops resolved reminders, so it holds just-completed ones open while it's
  // on screen, until the list goes idle (`reminder-linger`).
  useRemindersSurfacePresence()
  const { listRef, leavingIds } = useRetireLingersWhenIdle(useSendClearedTodos())
  useTodosUndoToast()
  // Use the staged-action cache so titles and integration icons match the output view.
  const { allActions } = useVisibleStagedActions()
  const actions = useMemo(() => new Map(allActions.map((action) => [action.id, action])), [allActions])
  const isNotification = useCallback((chat: SidepanelChat) => isNotificationChat(chat, actions), [actions])
  const isPinned = useCallback((chat: SidepanelChat) => isPinnedChat(chat, actions), [actions])
  useForYouExecutedLinger(displayChats, feedKey, actions)

  const { setPinned: setReminderPinned } = useReminderActions()
  const setActionsPinned = useSetStagedActionsPinned()
  const setTodoPinned = (todo: TodoEntry, pinned: boolean) => {
    if (todo.kind === 'reminder') {
      setReminderPinned(todo.id, pinned)
    } else {
      setActionsPinned(todo.chat.stagedActionIds, pinned)
    }
  }

  const now = useDueClock(activeItems)

  const inProgress = useMemo(() => displayChats.filter(isPreviewlessStreamingChat), [displayChats])
  const [limits, setLimits] = useAtom(todoGroupLimitsAtom)
  const [notificationsOpen, setNotificationsOpen] = useAtom(notificationsTodoOpenAtom)
  const pinnedPreference = useSortPreference(PINNED_TODOS_SORT_KEY)
  const groups = useMemo(
    () => groupTodos(displayChats, activeItems, hasNextPage, isNotification, now, isPinned, pinnedPreference?.order),
    [displayChats, activeItems, hasNextPage, isNotification, now, isPinned, pinnedPreference],
  )
  const unpinnedTodos = useMemo(() => groups.flatMap((group) => (group.id === 'pinned' ? [] : group.todos)), [groups])
  const rows = useMemo(
    () =>
      listTodoRows(groups, inProgress.length > 0, {
        limits,
        notificationsOpen,
        moreToLoad: hasNextPage,
        leavingIds,
      }),
    [groups, inProgress.length, limits, notificationsOpen, hasNextPage, leavingIds],
  )
  const showMore = useCallback(
    (id: TodoGroup['id']) =>
      setLimits((current) => new Map(current).set(id, todoGroupLimit(current, id) + TODO_GROUP_PAGE_SIZE)),
    [setLimits],
  )
  const collapse = useCallback(
    (id: TodoGroup['id']) =>
      setLimits((current) => {
        const next = new Map(current)
        next.delete(id)
        return next
      }),
    [setLimits],
  )
  const toggleNotifications = useCallback(() => setNotificationsOpen((open) => !open), [setNotificationsOpen])

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage().catch((error) => Sentry.captureException(error))
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])
  const todosCount = groups.find((group) => group.id === 'todos')?.todos.length ?? 0
  const needsMoreTodos = todosCount < todoGroupLimit(limits, 'todos')
  useEffect(() => {
    if (needsMoreTodos) loadMore()
  }, [needsMoreTodos, loadMore])

  if (isLoadingChats && isLoadingReminders) {
    return (
      <LoadingState className="w-full px-3 pb-3 pt-1">
        <LoadingState.Spinner />
        <LoadingState.Text messages={LOADING_MESSAGES} />
      </LoadingState>
    )
  }

  return (
    <div ref={listRef} className="flex flex-col gap-1.5">
      <PinnedTodoReorder
        todos={groups.find((group) => group.id === 'pinned')?.todos ?? []}
        unpinnedTodos={unpinnedTodos}
        onPin={(todo) => setTodoPinned(todo, true)}
        onUnpin={(todo) => setTodoPinned(todo, false)}
      >
        <TodoList
          rows={rows}
          inProgress={inProgress}
          actions={actions}
          feedKey={feedKey}
          onShowMore={showMore}
          onCollapse={collapse}
          onToggleNotifications={toggleNotifications}
        />
      </PinnedTodoReorder>
      {isChatsError && <p className="text-danger-subtle type-ui-sm px-3 py-1">Couldn’t load to-dos. Try refreshing.</p>}
    </div>
  )
})

/** The to-dos virtualized against Home's scroller. */
function TodoList({
  rows,
  inProgress,
  actions,
  feedKey,
  onShowMore,
  onCollapse,
  onToggleNotifications,
}: {
  rows: Array<TodoRow>
  inProgress: Array<SidepanelChat>
  actions: ReadonlyMap<string, StagedAction>
  feedKey: string
  onShowMore: (id: TodoGroup['id']) => void
  onCollapse: (id: TodoGroup['id']) => void
  onToggleNotifications: () => void
}) {
  const [listElement, setListElement] = useState<HTMLDivElement | null>(null)
  const scrollParent = useMemo(() => findScrollParent(listElement), [listElement])
  const findInPageOpen = useSidepanelFindInPageOpen()

  const rowDropSections = useMemo(() => {
    let section: 'pinned' | 'todos' | undefined
    return rows.map((row) => {
      if (row.kind === 'label') {
        section = row.id === 'pinned' ? 'pinned' : 'todos'
      }
      return section
    })
  }, [rows])

  return (
    <div ref={setListElement}>
      {scrollParent && (
        <Virtuoso
          customScrollParent={scrollParent}
          data={rows}
          computeItemKey={(_index, row) => todoRowKey(row)}
          itemContent={(index, row) => {
            const section = rowDropSections[index]
            const content = (
              // The padding stays outside the presence, as Virtuoso can't measure a row at zero height.
              <div className="pb-0.5">
                <TodoRowPresence presence={'presence' in row ? row.presence : undefined}>
                  <TodoListRow
                    row={row}
                    inProgress={inProgress}
                    actions={actions}
                    feedKey={feedKey}
                    onShowMore={onShowMore}
                    onCollapse={onCollapse}
                    onToggleNotifications={onToggleNotifications}
                  />
                </TodoRowPresence>
              </div>
            )
            return section === 'pinned' || section === 'todos' ? (
              <PinnedTodoDropZone
                section={section}
                first={rowDropSections[index - 1] !== section}
                last={rowDropSections[index + 1] !== section}
              >
                {content}
              </PinnedTodoDropZone>
            ) : (
              content
            )
          }}
          increaseViewportBy={findInPageOpen ? MOUNT_ALL_VIEWPORT_BY : NORMAL_VIEWPORT_BY}
        />
      )}
    </div>
  )
}

/**
 * Eases a row in or out by height, so the rows around it glide rather than jump. Clips only while
 * moving, as a resting row's focus ring and mark target run past its edges.
 */
function TodoRowPresence({ presence, children }: { presence?: TodoRowPresenceKind; children: ReactNode }) {
  return (
    <div
      className={twMerge(
        'grid grid-cols-[minmax(0,1fr)] grid-rows-[1fr] transition-[grid-template-rows,opacity] duration-200 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
        presence === 'leaving' && 'pointer-events-none grid-rows-[0fr] opacity-0',
        presence === 'entering' && '[@starting-style]:grid-rows-[0fr] [@starting-style]:opacity-0',
      )}
    >
      <div className={twMerge('min-h-0 min-w-0', presence && 'overflow-hidden')}>{children}</div>
    </div>
  )
}

const TodoListRow = memo(function TodoListRow({
  row,
  inProgress,
  actions,
  feedKey,
  onShowMore,
  onCollapse,
  onToggleNotifications,
}: {
  row: TodoRow
  inProgress: Array<SidepanelChat>
  actions: ReadonlyMap<string, StagedAction>
  feedKey: string
  onShowMore: (id: TodoGroup['id']) => void
  onCollapse: (id: TodoGroup['id']) => void
  onToggleNotifications: () => void
}) {
  switch (row.kind) {
    case 'label':
      return (
        <HomeHeading
          count={row.count > 0 ? `${row.count}${row.countIsAtLeast ? '+' : ''}` : undefined}
          action={row.expanded && <HomeHeadingCollapse section={row.label} onClick={() => onCollapse(row.id)} />}
        >
          {row.label}
        </HomeHeading>
      )
    case 'new-reminder':
      return <NewReminderTodo />
    // The rows' 8px gap after the 16px indicator, so the label starts where the titles do.
    case 'in-progress':
      return <ChatsInProgress chats={inProgress} className={`gap-2 ${SIDEPANEL_FILLED_HOVER_SURFACE}`} />
    case 'notifications':
      return <NotificationsTodo todos={row.todos} open={row.open} feedKey={feedKey} onToggle={onToggleNotifications} />
    case 'todo':
      return (
        <DraggableTodoRow todo={row.todo} disabled={row.presence === 'leaving'}>
          {row.todo.kind === 'chat' ? (
            <NestedTodo nested={row.nested}>
              <ForYouChatRow
                chat={row.todo.chat}
                actions={actions}
                sourceLabel="Home"
                feedKey={feedKey}
                position={row.todo.feedPosition}
                appearance="todo"
                marked={!row.nested}
              />
            </NestedTodo>
          ) : (
            <ReminderCell item={row.todo.reminder} variant="row" mark="todo" opensIn="popover" />
          )}
        </DraggableTodoRow>
      )
    case 'more':
      return <HomeViewMore onClick={() => onShowMore(row.id)} />
  }
})

function NewReminderTodo() {
  const { addItem } = useReminderActions()
  return <ReminderInput onSubmit={addItem} appearance="todo" />
}

/**
 * A row under the notifications to-do, which ticks for it, so it has no mark. A guide curves into it
 * instead, and the 24px indent lines its title up with the to-do's.
 */
function NestedTodo({ nested, children }: { nested?: 'branch' | 'last'; children: ReactNode }) {
  if (!nested) return children
  return (
    <div className="relative pl-6">
      {/* Bottoms out at 19px, the middle of the 20px first line under the row's 1px border and 8px padding. */}
      <span aria-hidden="true" className="absolute -top-0.5 left-5 h-[22px] w-2.5 rounded-bl-md border-b border-l" />
      {/* Starts where the elbow's 8px corner curves, so the translucent hairlines never overlap and darken. */}
      {nested === 'branch' && <span aria-hidden="true" className="absolute bottom-0 left-5 top-3 border-l" />}
      {children}
    </div>
  )
}
